import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Bot, Box, BrainCircuit, Compass, KeyRound, Route, Network, Workflow } from "lucide-react";
import { memo } from "react";
import { nodeRegistry } from "./nodeRegistry";
import type { CanvasKind, ControlCanvasNode } from "./types";

const icons = {
  llm: BrainCircuit,
  mcp: Box,
  a2a: Network,
  model: Route,
  agent: Bot,
  key: KeyRound,
  task: Workflow,
  discovery: Compass
} satisfies Record<CanvasKind, typeof Bot>;

export const ControlNode = memo(function ControlNode({
  data,
  selected
}: NodeProps<ControlCanvasNode>) {
  const definition = nodeRegistry[data.kind];
  const Icon = icons[data.kind];
  return (
    <article
      className={`control-node control-node-${data.kind}${selected ? " is-selected" : ""}`}
      style={{ "--node-accent": definition.color } as React.CSSProperties}
    >
      {data.inputs.map((input, index) => (
        <Handle
          key={input.id}
          id={`in:${input.id}`}
          type="target"
          position={Position.Left}
          style={{ top: 43 + index * 18 }}
          title={`Accepts ${input.label}`}
          isConnectable={false}
        />
      ))}
      <header>
        <span className="control-node-icon">
          <Icon size={15} aria-hidden="true" />
        </span>
        <span>{definition.label}</span>
        <i className={`node-pulse pulse-${data.status.replaceAll(" ", "-")}`} title={data.status} />
      </header>
      <div className="control-node-body">
        <strong>{data.title}</strong>
        <small>{data.subtitle}</small>
      </div>
      <footer>
        <span>{data.status.replaceAll("_", " ")}</span>
        {data.outputs.length > 0 && (
          <span className="port-summary">
            {data.outputs.map((output) => output.label).join(" / ")}
          </span>
        )}
      </footer>
      {data.outputs.map((output, index) => (
        <Handle
          key={output.id}
          id={`out:${output.id}`}
          type="source"
          position={Position.Right}
          style={{ top: 43 + index * 18 }}
          title={`Can derive ${output.label}`}
          isConnectable={false}
        />
      ))}
    </article>
  );
});
